<template>
  <div class="portfolio">

    <!-- HERO -->
    <section class="hero">
      <img class="hero-avatar" src="/icons/zane.png" alt="Zane portrait" />

      <h1>iOS Engineer & SwiftUI Specialist.<br />Quality apps delivered well.</h1>

      <p class="hero-sub">
        I’m Zane — an iOS engineer focused on SwiftUI, clean architecture, and polished delivery. 
        I’ve shipped production work for ABC, Service Victoria, and RFDS, with a reputation for 
        reliable, high-quality engineering.
      </p>

      <a class="cta cta-hero" style="text-decoration: none;" href="mailto:hello@zanecarter.com">Work With Me</a>
    </section>


    <section class="appstore-section">
      <h3 class="section-title">Featured</h3>

      <div class="appstore-grid">

        <!-- Mixpanel Event Viewer -->
        <div class="appstore-card">
          <img class="featured-app-icon" src="/icons/eventviewer-icon.png" alt="Event Viewer Icon" />

          <h4>Event Viewer</h4>
          <p>
            A lightweight SwiftUI tool for inspecting Mixpanel analytics events in real time. Built for 
            developers who need fast filtering, grouping, and insight during debugging or production monitoring.
          </p>

          <a class="cta cta-hero" style="text-decoration: none;" href="https://apps.apple.com/au/app/scout-mixpanel-event-viewer/id6444118357">Download</a>

        </div>

        <!-- WeedScout -->
        <div class="appstore-card">
          <img class="featured-app-icon" src="/icons/weedscout-icon.png" alt="WeedScout Icon" />

          <h4>WeedScout</h4>
          <p>
            AI weed identification and management for Australia.  
            Offline model, 1,200+ species, curated datasets, and integrated APVMA herbicide lookup.  
            Designed for land managers, homeowners, and councils.
          </p>
          <a class="cta cta-hero" style="text-decoration: none;" href="https://weedscout.com.au">Learn More</a>

        </div>

      </div>
    </section>

    <br></br>
    <br></br>
    
    <section class="projects">
      <h3 class="section-title">Professional Work</h3>

      <!-- ABC -->
      <div class="project-card">
        <div class="img-wrapper">
          <img src="/images/abc.png" alt="ABC News / iView project screenshot" />
        </div>
        <div class="project-info project-content">
          <h4>Australian Broadcasting Corporation (ABC)</h4>
          <p>
            iOS engineering across core components for one of Australia’s largest media organisations.  
            Contributed SwiftUI features, accessibility improvements, analytics upgrades, and modernisation 
            of legacy UIKit flows. Ensured stability and performance for high-traffic public apps.
          </p>
        </div>
      </div>

      <!-- Service Victoria -->
      <div class="project-card">
        <div class="img-wrapper">
          <img src="/images/sv.png" alt="Service Victoria screenshot" />
        </div>
        <div class="project-info project-content">
          <h4>Service Victoria</h4>
          <p>
            Delivered citizen-facing features during peak usage periods.  
            Built secure, reliable flows and polished SwiftUI components used statewide.  
            Contributed to smoother navigation, improved error handling, and high-performance UI under load.
          </p>
        </div>
      </div>

      <!-- WeedScout -->
      <div class="project-card">
        <div class="img-wrapper">
          <img src="/images/weedscout.png" alt="WeedScout app screenshot" />
        </div>
        <div class="project-info project-content">
          <h4>WeedScout (Founder & Lead Engineer)</h4>
          <p>
            AI weed identification and management app tailored for Australia.  
            Built a completely offline species classifier, designed the entire SwiftUI interface,  
            and developed supporting data pipelines (ML training, APVMA herbicide integration, 
            state weed database ingestion).  
            A full end-to-end product from model training → app architecture → UX polish.
          </p>
        </div>
      </div>

    </section>

    <!-- ABOUT ME -->
    <section class="about">
      <h3 class="section-title">About Me</h3>

      <img class="about-photo" src="/icons/zane.png" alt="Zane portrait" />

      <p>
        I'm an iOS Engineer transitioning into AI/ML and Agricultural Science.  
        I build apps, ML pipelines, and intelligent tools that solve real-world problems — 
        from weed identification to field-ready agronomy tools.
      </p>
      <p>
        I value clean architecture, thoughtful UX, and shipping products that feel fast, reliable, 
        and delightful to use.
      </p>
    </section>


    <!-- CONTACT -->
    <section class="contact">
      <h3 class="section-title">Contact</h3>

      <p class="contact-blurb">
        I’m currently open for freelance iOS roles — SwiftUI, app polishing, architecture cleanup, 
        or complete end-to-end app builds.
      </p>

      <a class="cta cta-hero" style="text-decoration: none;" href="mailto:hello@zanecarter.com">Work With Me</a>

      <br></br>
      <br></br>
      <br></br>
      <div class="contact-links">
        <a href="mailto:hello@zanecarter.com" class="contact-icon">
          <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/maildotru.svg" alt="Email" />
        </a>

        <a href="https://github.com/zane-carter" target="_blank" class="contact-icon">
          <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/github.svg" alt="GitHub" />
        </a>

        <a href="https://www.linkedin.com/in/zane-carter" target="_blank" class="contact-icon">
          <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/linkedin.svg" alt="LinkedIn" />
        </a>
      </div>
    </section>

  </div>
</template>

<script>
export default { name: "PortfolioPage" };
</script>

<style scoped>
.portfolio {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1.2rem;
  font-family: -apple-system, BlinkMacSystemFont, "Inter", sans-serif;
  color: #1e1e1e;
}

/* --- HERO --- */
.hero {
  text-align: center;
  padding: 6rem 1rem 4rem;
}

.hero-avatar {
  width: 130px;
  height: 130px;
  border-radius: 9999px;
  object-fit: cover;
  display: block;
  margin: 0 auto 1.5rem;
}

.hero h1 {
  font-size: 2.4rem;
  font-weight: 800;
  line-height: 1.15;
}

.hero-sub {
  max-width: 650px;
  margin: 1.2rem auto;
  font-size: 1.05rem;
  opacity: 0.85;
  line-height: 1.6;
}

.cta-hero {
  margin: 1rem auto 0 auto; /* center button fix */
}

.cta {
  background: #222;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 0.55rem 1.2rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-block;
  width: auto;
  max-width: fit-content;
  white-space: nowrap;
  transition: background 0.2s ease;
}

.cta:hover { background: #444; }

/* --- Section Titles --- */
.section-title {
  margin: 1rem 0 1.5rem;
  font-size: 1.8rem;
  font-weight: 700;
}


/* --- FEATURED --- */
.appstore-grid {
  display: grid;
  gap: 1.2rem;
}

.appstore-card {
  background: #fff;
  padding: 1.4rem;
  border-radius: 16px;
  box-shadow: 0 3px 10px rgba(0,0,0,0.08);
  text-align: center;
}

.featured-app-icon {
  width: 80px;
  height: 80px;
  border-radius: 18px;
  object-fit: cover;
  margin-bottom: 0.8rem;
}

.appstore-badge {
  margin-top: 0.6rem;
  height: 42px;
}


/* --- PROFESSIONAL WORK --- */
.project-card {
  background: #fff;
  border-radius: 16px;
  padding: 1rem;
  margin-bottom: 1.8rem;
  box-shadow: 0 3px 10px rgba(0,0,0,0.08);
}

/* 4:3 screenshot */
.img-wrapper {
  width: 100%;
  padding-top: 75%;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #e5e5ea;
  margin-bottom: 1rem;
}

.img-wrapper img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.project-info h4 {
  font-size: 1.25rem;
  margin-bottom: 0.25rem;
  font-weight: 700;
}

/* --- ABOUT --- */
.about {
  text-align: center;
  margin-bottom: 2.5rem;
}

.about-photo {
  width: 180px;
  height: 180px;
  border-radius: 9999px;
  object-fit: cover;
  margin-bottom: 1.4rem;
}

/* --- CONTACT --- */
.contact {
  text-align: center;
  margin-bottom: 2.5rem;
}

.contact-blurb {
  max-width: 600px;
  margin: 0 auto 1rem;
  font-size: 1.1rem;
  opacity: 0.9;
  line-height: 1.55;
}

.contact-links {
  display: flex;
  justify-content: center;
  gap: 1.2rem;
  margin-top: 1.2rem;
}
.contact-links {
  margin-top: 1.2rem;
  margin-bottom: 2.5rem; /* NEW SPACING */
}
.contact-icon img {
  width: 32px;
  height: 32px;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.contact-icon img:hover { opacity: 1; }

.project-card {
  padding: 0; /* remove inner padding so image reaches the edges */
  overflow: hidden;
}

.img-wrapper {
  border-radius: 16px 16px 0 0; /* rounded corners only on top */
  margin-bottom: 0; /* remove previous spacing */
}

.project-content {
  padding: 1.2rem 1.2rem 1.4rem; /* NEW padding for text area */
}

.appstore-grid {
  display: grid;
  gap: 1.2rem;
  grid-template-columns: 1fr;
}

@media (min-width: 650px) {
  .appstore-grid {
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  }
}

.appstore-card {
  max-width: 420px;
  margin: 0 auto;
}

.appstore-section {
  margin-top: 1rem;
}

.img-wrapper img {
  border-radius: 16px 16px 0 0;
}
</style>